import { normalizeSupplementName } from './holobiomeKits';

/**
 * A practitioner's change to the Recommended Supplements list on ONE test.
 *
 * The card is built from what the blood work pointed to, which is everything
 * the analysis can say and nothing it can't: a product no pattern reaches
 * (Digestion is authored but unplumbed, the Neuro gut-brain formulas have no
 * marker behind them at all), a product the client is already taking, a serving
 * the practitioner has changed for this client. All three are edits to a list,
 * so all three are one type.
 *
 *   'added'     the product is on the list because the practitioner put it
 *               there. It has a row of its own.
 *   'override'  the analysis put it on the list; this only changes what the row
 *               says.
 *
 * THE OVERRIDE IS APPLIED, NEVER A REASON TO ADD A ROW. If the test's values
 * are later corrected and the pattern behind a product stops firing, its
 * override stays here and simply stops applying — resurrecting the row would
 * put a bottle on a client's list that nothing recommended.
 *
 * WHAT IS STORED IS THE OVERRIDE, NOT A COPY OF THE CATALOG ROW. `tag` points
 * at the cat 500xxx supplement row, and description/link/dose are present only
 * where the practitioner typed something. So a description improved in the
 * catalog improves every report that didn't override it, which is already true
 * of the rows the analysis puts on this card — an added row that snapshotted
 * its text would slowly drift from an identified row for the same product.
 * (This is the opposite of the custom-marker rule, where the per-test snapshot
 * is authoritative because a range is what a result was interpreted against. A
 * supplement description is product copy.)
 */
export type SupplementEditOrigin = 'added' | 'override';

export class SupplementEdit {
  /** Display name, and the key every lookup matches on. */
  name: string;

  origin: SupplementEditOrigin = 'override';

  /** The cat 500xxx catalog tag, when the product came from the picker. */
  tag?: string;

  /** Practitioner text. Undefined means "whatever the catalog says". */
  description?: string;
  link?: string;
  dose?: string;

  constructor(supplementEdit?: Partial<SupplementEdit>) {
    if (!supplementEdit) {
      return;
    }
    Object.keys(supplementEdit).forEach(key => {
      this[key] = supplementEdit[key];
    });
    // A row with no origin predates nothing — but an edit that lost its origin
    // must not invent a product, so it reads as an override.
    this.origin = supplementEdit.origin === 'added' ? 'added' : 'override';
  }
}

/** The lookup key for an edit or a product name. */
export const supplementEditKey = (name: string): string =>
  normalizeSupplementName(name);

/**
 * Index a test's edits by product name.
 *
 * Later entries win, so a list that somehow carries two edits for one product
 * (a hand-fixed record, an older build) resolves rather than rendering twice.
 */
export function indexSupplementEdits(
  edits: SupplementEdit[] | undefined,
): Map<string, SupplementEdit> {
  const byName = new Map<string, SupplementEdit>();
  (edits || []).forEach(edit => {
    const key = edit && edit.name ? supplementEditKey(edit.name) : '';
    if (key) {
      byName.set(key, edit instanceof SupplementEdit ? edit : new SupplementEdit(edit));
    }
  });
  return byName;
}

/** The edits that put a product on the list, in the order they were added. */
export function addedSupplementEdits(
  edits: SupplementEdit[] | undefined,
): SupplementEdit[] {
  return (edits || []).filter(edit => edit && edit.origin === 'added' && !!edit.name);
}

/**
 * Apply an edit to one field. Undefined and empty both mean "not overridden":
 * clearing a box restores the catalog text rather than printing a blank cell,
 * which is also what Reset does.
 */
export function applySupplementOverride(
  override: string | undefined,
  fallback: string,
): string {
  const text = (override || '').trim();
  return text || fallback;
}

/**
 * Add or replace one product's edit, returning a NEW array.
 *
 * Callers hold this in React state and hand it to the panel, so it has to be a
 * fresh array rather than a mutation — and the store writes the saved record
 * back over its list row (BloodworkEntryStore.refreshSavedBloodwork), which is
 * what keeps an assigned array from being lost the next time the test is
 * edited.
 */
export function withSupplementEdit(
  edits: SupplementEdit[] | undefined,
  edit: SupplementEdit,
): SupplementEdit[] {
  const key = supplementEditKey(edit.name);
  const rest = (edits || []).filter(
    existing => supplementEditKey(existing.name) !== key,
  );
  const existing = (edits || []).find(
    candidate => supplementEditKey(candidate.name) === key,
  );
  // An override typed on a row the practitioner had added must not demote that
  // row to an override, which would take the product off the list.
  const origin = existing && existing.origin === 'added' ? 'added' : edit.origin;
  return [...rest, new SupplementEdit({ ...edit, origin })];
}

/**
 * Put one row back to what the catalog says.
 *
 * An ADDED row keeps its place on the list: the practitioner asked for that
 * product, and only its wording is being reset. Dropping the edit outright
 * would take the product off the client's list, which is what Remove is for and
 * would read as losing work rather than undoing a change.
 */
export function resetSupplementEdit(
  edits: SupplementEdit[] | undefined,
  name: string,
): SupplementEdit[] {
  const key = supplementEditKey(name);
  const existing = (edits || []).find(
    candidate => supplementEditKey(candidate.name) === key,
  );
  if (!existing || existing.origin !== 'added') {
    return withoutSupplementEdit(edits, name);
  }
  return (edits || []).map(edit =>
    supplementEditKey(edit.name) === key
      ? new SupplementEdit({ name: edit.name, origin: 'added', tag: edit.tag })
      : edit,
  );
}

/**
 * Rename a product the practitioner typed in themselves.
 *
 * ONLY EVER A HAND-TYPED ROW. A row picked out of the catalog is named by the
 * catalog (addedProduct resolves its name from the tag), so renaming one here
 * would store a name the card then ignores — the caller decides that, and this
 * refuses a rename onto a name already in use either way.
 *
 * The edit keeps its PLACE in the array rather than being removed and re-added:
 * added rows print in the order they were added, and withSupplementEdit moves
 * what it touches to the end, so renaming through it would silently reshuffle
 * the client's list.
 */
export function renameSupplementEdit(
  edits: SupplementEdit[] | undefined,
  from: string,
  to: string,
): SupplementEdit[] {
  const list = edits || [];
  const fromKey = supplementEditKey(from);
  const name = (to || '').trim();
  const toKey = supplementEditKey(name);
  if (!toKey || toKey === fromKey) return list;
  if (list.some(edit => supplementEditKey(edit.name) === toKey)) return list;
  return list.map(edit =>
    supplementEditKey(edit.name) === fromKey
      ? new SupplementEdit({ ...edit, name })
      : edit,
  );
}

/** Drop one product's edit entirely — Remove on an added row. */
export function withoutSupplementEdit(
  edits: SupplementEdit[] | undefined,
  name: string,
): SupplementEdit[] {
  const key = supplementEditKey(name);
  return (edits || []).filter(edit => supplementEditKey(edit.name) !== key);
}

/**
 * Is this edit worth storing?
 *
 * An override that overrides nothing is noise on the record: it survives into
 * the PDF window's copy of the test and into every diff of it, and it makes
 * "has this test been edited" a lie. An ADDED row always counts — the product
 * being on the list is the edit.
 */
export function isMeaningfulSupplementEdit(edit: SupplementEdit): boolean {
  if (!edit || !edit.name || !edit.name.trim()) {
    return false;
  }
  if (edit.origin === 'added') {
    return true;
  }
  return !!(
    (edit.description && edit.description.trim()) ||
    (edit.link && edit.link.trim()) ||
    (edit.dose && edit.dose.trim())
  );
}
